import { collection, doc, query, where, orderBy, getDocs, type Query } from '../../libs/firestore-wrapper';
import db_config from '../../configs/db_config';
import { db } from '../../libs/firebase';
import { Food, MenuType, UserMenuCategory, UserMenuFoodItem, UserMenuListData, UserMenuRecommendedFood } from '../../pages/UserMenus/userMenu.types';
import { ICategoryDetails } from '../../pages/MenuManagement/menu.types';

export function getAllMenuTypes(): MenuType[] {
  return [MenuType.REGULAR_ORDERS, MenuType.REGULAR_PRE_ORDERS, MenuType.SPECIAL_ORDERS, MenuType.SPECIAL_PRE_ORDERS];
}

export function getMenuTypePathSegment(menu_type: MenuType): string {
  const mapping: Record<MenuType, string> = {
    [MenuType.REGULAR_ORDERS]: 'regular_order',
    [MenuType.REGULAR_PRE_ORDERS]: 'regular_pre_order',
    [MenuType.SPECIAL_ORDERS]: 'special_order',
    [MenuType.SPECIAL_PRE_ORDERS]: 'special_pre_order',
  };
  return mapping[menu_type];
}

export function getFoodtrucksByStadiumId(stadiumId: string): Query {
  return query(collection(db, db_config.DB_TABLE_RESTAURANT_CONFIG), where('stadium_id', '==', stadiumId));
}

export async function getAllStadiumCategories(stadiumId: string): Promise<ICategoryDetails[]> {
  const foodtrucksQuery = getFoodtrucksByStadiumId(stadiumId);
  const foodtrucksSnapshot = await getDocs(foodtrucksQuery);
  const foodtruckIds = foodtrucksSnapshot.docs.map((d) => d.id);

  if (foodtruckIds.length === 0) return [];

  const batchSize = 10;
  const batches: string[][] = [];
  for (let i = 0; i < foodtruckIds.length; i += batchSize) {
    batches.push(foodtruckIds.slice(i, i + batchSize));
  }

  const snapshots = await Promise.all(
    batches.map((batch) => {
      const categoryQuery = query(collection(db, db_config.DB_TABLE_ALL_CATEGORIES), where('restaurantId', 'in', batch), orderBy('order'));
      return getDocs(categoryQuery);
    })
  );

  const allCategories: ICategoryDetails[] = [];
  snapshots.forEach((snap) => {
    snap.docs.forEach((d) => {
      allCategories.push({ id: d.id, ...d.data() } as ICategoryDetails);
    });
  });

  return allCategories;
}

export async function getUserMenuList(stadiumId: string, menu_type: MenuType): Promise<UserMenuListData> {
  const menuTypePath = getMenuTypePathSegment(menu_type);
  const menuListPath = `${db_config.DB_TABLE_USER_MENU_LIST}/${stadiumId}/${menuTypePath}`;

  try {
    const categoriesSnapshot = await getDocs(collection(db, menuListPath));

    const categories: UserMenuCategory[] = categoriesSnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        order: data.order || 0,
        food_ids: (data.food_ids || []).map((foodItem: UserMenuFoodItem) => ({
          id: foodItem.id || '',
          banner: foodItem.banner || false,
          banner_order: typeof foodItem.banner_order === 'number' ? foodItem.banner_order : undefined,
          enabled: foodItem.enabled || false,
          order: foodItem.order || 0,
          recommended_food: (foodItem.recommended_food || []).map((recFood: UserMenuRecommendedFood) => ({
            id: recFood.id || '',
            enabled: recFood.enabled || false,
            order: recFood.order || 0,
          })),
        })),
      };
    });

    return { categories };
  } catch (error) {
    console.error('Error fetching user menu list:', error);
    return { categories: [] };
  }
}

export async function deleteCategoryFromUserMenuList(stadiumId: string, categoryId: string): Promise<void> {
  const { writeBatch: createBatch } = await import('../../libs/firestore-wrapper');
  const batch = createBatch();
  const menuTypes = getAllMenuTypes();

  for (const menuType of menuTypes) {
    const menuTypePath = getMenuTypePathSegment(menuType);
    const categoryPath = `${db_config.DB_TABLE_USER_MENU_LIST}/${stadiumId}/${menuTypePath}/${categoryId}`;
    const categoryRef = doc(db, categoryPath);
    batch.delete(categoryRef);
  }

  try {
    await batch.commit();
  } catch (error) {
    console.error('Error deleting category from user_menu_list:', error);
  }
}

export async function moveFoodsBetweenCategoriesInUserMenuList(stadiumId: string, foodIds: string[], fromCategoryIds: string[], toCategoryIds: string[]): Promise<void> {
  const { getDoc: getDocFS, writeBatch: createBatch, setDoc } = await import('../../libs/firestore-wrapper');
  const menuTypes = getAllMenuTypes();

  try {
    const userMenuListRef = doc(db, db_config.DB_TABLE_USER_MENU_LIST, stadiumId);
    const userMenuListDoc = await getDocFS(userMenuListRef);
    if (!userMenuListDoc.exists()) {
      await setDoc(userMenuListRef, { id: stadiumId });
    }
  } catch (error) {
    console.error('Error creating user_menu_list parent doc:', error);
  }

  for (const menuType of menuTypes) {
    const menuTypePath = getMenuTypePathSegment(menuType);
    const basePath = `${db_config.DB_TABLE_USER_MENU_LIST}/${stadiumId}/${menuTypePath}`;

    try {
      const batch = createBatch();

      for (const categoryId of fromCategoryIds) {
        const categoryRef = doc(db, `${basePath}/${categoryId}`);
        const categoryDoc = await getDocFS(categoryRef);

        if (categoryDoc.exists()) {
          const data = categoryDoc.data();
          const foodIdsArray = data.food_ids || [];

          const updatedFoodIds = foodIdsArray.filter((foodItem: UserMenuFoodItem) => {
            return !foodIds.includes(foodItem.id);
          });

          if (updatedFoodIds.length !== foodIdsArray.length) {
            batch.update(categoryRef, { food_ids: updatedFoodIds });
          }
        }
      }

      for (const categoryId of toCategoryIds) {
        const categoryRef = doc(db, `${basePath}/${categoryId}`);
        const categoryDoc = await getDocFS(categoryRef);

        let foodIdsArray: UserMenuFoodItem[] = [];

        if (categoryDoc.exists()) {
          const data = categoryDoc.data();
          foodIdsArray = data.food_ids || [];
        }

        let hasChanges = false;
        foodIds.forEach((foodId) => {
          if (!foodIdsArray.find((f: UserMenuFoodItem) => f.id === foodId)) {
            foodIdsArray.push({
              id: foodId,
              banner: false,
              enabled: true,
              order: foodIdsArray.length,
              recommended_food: [],
            });
            hasChanges = true;
          }
        });

        if (hasChanges) {
          batch.set(
            categoryRef,
            {
              food_ids: foodIdsArray,
            },
            { merge: true }
          );
        }
      }

      await batch.commit();
    } catch (error) {
      console.error(`Error moving foods in user_menu_list for ${menuType}:`, error);
    }
  }
}

export async function addCategoriesToUserMenuList(stadiumId: string, categories: { id: string; order: number }[]): Promise<void> {
  if (categories.length === 0) return;

  const { writeBatch, doc, getDoc: getDocFS, setDoc } = await import('../../libs/firestore-wrapper');
  const menuTypes = getAllMenuTypes();
  const batch = writeBatch();

  try {
    const parentRef = doc(db, db_config.DB_TABLE_USER_MENU_LIST, stadiumId);
    const parentDoc = await getDocFS(parentRef);
    if (!parentDoc.exists()) {
      await setDoc(parentRef, { id: stadiumId });
    }
  } catch (err) {
    console.error('Error creating user_menu_list parent doc:', err);
  }

  for (const menuType of menuTypes) {
    const menuTypePath = getMenuTypePathSegment(menuType);
    const basePath = `${db_config.DB_TABLE_USER_MENU_LIST}/${stadiumId}/${menuTypePath}`;

    for (const cat of categories) {
      const catRef = doc(db, `${basePath}/${cat.id}`);
      batch.set(
        catRef,
        {
          id: cat.id,
          order: cat.order,
          food_ids: [],
        },
        { merge: true }
      );
    }
  }

  try {
    await batch.commit();
  } catch (error) {
    console.error('Error adding categories to user_menu_list:', error);
  }
}

export async function deleteCategoryFromAllUserMenuLists(stadiumId: string, categoryId: string): Promise<void> {
  const { writeBatch, doc } = await import('../../libs/firestore-wrapper');
  const menuTypes = getAllMenuTypes();
  const batch = writeBatch();

  for (const menuType of menuTypes) {
    const menuTypePath = getMenuTypePathSegment(menuType);
    const path = `${db_config.DB_TABLE_USER_MENU_LIST}/${stadiumId}/${menuTypePath}/${categoryId}`;
    const ref = doc(db, path);
    batch.delete(ref);
  }

  try {
    await batch.commit();
  } catch (error) {
    console.error('Error deleting category from all user_menu_lists:', error);
  }
}

export async function getStadiumMaxBannerQuantity(stadiumId: string): Promise<number | null> {
  const { getDoc: getDocFS, doc } = await import('../../libs/firestore-wrapper');
  try {
    const docRef = doc(db, db_config.DB_STADIUM_MANAGE, stadiumId);
    const docSnap = await getDocFS(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.max_banner_quantity !== undefined && data.max_banner_quantity !== null) {
        const parsed = Number(data.max_banner_quantity);
        if (!isNaN(parsed)) {
          return parsed;
        }
      }
    }
  } catch (error) {
    console.error('Error fetching max_banner_quantity:', error);
  }
  return null;
}

export async function getStadiumMaxRecommendQuantity(stadiumId: string): Promise<number | null> {
  const { getDoc: getDocFS, doc } = await import('../../libs/firestore-wrapper');
  try {
    const docRef = doc(db, db_config.DB_STADIUM_MANAGE, stadiumId);
    const docSnap = await getDocFS(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.max_recommend_quantity !== undefined && data.max_recommend_quantity !== null) {
        const parsed = Number(data.max_recommend_quantity);
        if (!isNaN(parsed)) {
          return parsed;
        }
      }
    }
  } catch (error) {
    console.error('Error fetching max recommend quantity:', error);
  }
  return null;
}
